import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchWorkflowScopesApi, type WorkflowScope } from '@/features/system/orchestrator/api/scopesApi';
import { timeAgo } from '@/utils/date';
import { useNow } from '@/utils/useNow';

const SCOPE_NAMESPACE = 'media.audio';
const DEFAULT_LIMIT = 5000;

function safeText(v: unknown): string {
  return String(v ?? '').trim();
}

function pickString(obj: any, ...keys: string[]): string {
  for (const k of keys) {
    const s = safeText(obj?.[k]);
    if (s) return s;
  }
  return '';
}

function normalizeItems(raw: any): WorkflowScope[] {
  if (Array.isArray(raw)) return raw as WorkflowScope[];
  if (Array.isArray(raw?.items)) return raw.items as WorkflowScope[];
  if (Array.isArray(raw?.rows)) return raw.rows as WorkflowScope[];
  return [];
}

export default function WorkflowMediaAudioSplitPanel() {
  const now = useNow(5_000);
  const [textFilter, setTextFilter] = useState('');

  const query = useQuery({
    queryKey: ['developer', 'workflowRunner', 'media.audio.split', 'scopes', { limit: DEFAULT_LIMIT }] as const,
    queryFn: async () => {
      // Fetch all scopes; filter client-side by scope_namespace.
      return await fetchWorkflowScopesApi({ limit: DEFAULT_LIMIT });
    },
    staleTime: 10_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });

  const items = useMemo(() => {
    const all = normalizeItems(query.data);
    const wantNs = SCOPE_NAMESPACE;

    const filteredByNs = all.filter((s: any) => {
      const ns = pickString(s, 'scope_namespace', 'scopeNamespace');
      return ns === wantNs;
    });

    const ft = safeText(textFilter).toLowerCase();
    if (!ft) return filteredByNs;

    return filteredByNs.filter((s: any) => {
      const ns = pickString(s, 'scope_namespace', 'scopeNamespace').toLowerCase();
      const key = pickString(s, 'scope_key', 'scopeKey').toLowerCase();
      const run = pickString(s, 'run_id', 'workflow_run_id').toLowerCase();
      return ns.includes(ft) || key.includes(ft) || run.includes(ft);
    });
  }, [query.data, textFilter]);

  return (
    <div className="h-full flex flex-col gap-3 p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-neutral-200">media.audio.split</div>
          <div className="text-xs text-neutral-500">Scopes filtered by scope_namespace={SCOPE_NAMESPACE}</div>
        </div>

        <div className="flex items-center gap-2">
          <input
            className="h-9 w-[320px] rounded-md pac-glass px-3 text-sm text-neutral-200 placeholder:text-neutral-600"
            placeholder="Filter (scope_key / run_id)"
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-md border border-neutral-800 overflow-hidden">
        <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between">
          <div className="text-xs text-neutral-500">
            {query.isLoading ? 'Loading…' : `${items.length} scope(s)`}
          </div>
          <div className="text-xs text-neutral-600">Updated {timeAgo(now.toISOString())}</div>
        </div>

        {query.error ? (
          <div className="p-3 text-sm text-red-300">{String((query.error as any)?.message ?? query.error)}</div>
        ) : (
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-black">
                <tr className="text-left text-xs text-neutral-500">
                  <th className="px-3 py-2">scope_namespace</th>
                  <th className="px-3 py-2">scope_key</th>
                  <th className="px-3 py-2">run_id</th>
                  <th className="px-3 py-2">updated</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s: any, idx: number) => {
                  const ns = pickString(s, 'scope_namespace', 'scopeNamespace') || '—';
                  const key = pickString(s, 'scope_key', 'scopeKey') || '—';
                  const runId = pickString(s, 'run_id', 'workflow_run_id') || '—';
                  const updatedAt = pickString(s, 'updated_at', 'created_at') || '';

                  return (
                    <tr key={`${ns}:${key}:${runId}:${idx}`} className="border-t border-neutral-900">
                      <td className="px-3 py-2 font-mono text-xs text-neutral-300">{ns}</td>
                      <td className="px-3 py-2 font-mono text-xs text-neutral-200">{key}</td>
                      <td className="px-3 py-2 font-mono text-xs text-neutral-400">{runId}</td>
                      <td className="px-3 py-2 text-xs text-neutral-500">{updatedAt ? timeAgo(updatedAt) : '—'}</td>
                    </tr>
                  );
                })}

                {!query.isLoading && items.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-sm text-neutral-500" colSpan={4}>
                      No scopes found for namespace {SCOPE_NAMESPACE}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
